import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ExternalLink, MessageCircle } from "lucide-react";
import { CONTACTS } from "@/data/contacts";
import { useLang } from "@/i18n";

export default function LocationSection() {
  const { t } = useLang();
  return (
    <section id="dove-siamo" data-testid="location-section" className="relative py-28 md:py-40 px-6 md:px-10 bg-smoke">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-14 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">{t("loc_overline")}</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight" data-testid="location-title">
              {t("loc_title")}
            </h2>

            <div className="mt-10 space-y-5" data-testid="location-info">
              <InfoRow icon={<MapPin size={18} />} title={t("loc_address")} text={<>{CONTACTS.address}<br />{CONTACTS.city}</>} />
              <InfoRow icon={<Phone size={18} />} title={t("loc_cell")} text={<a href={`tel:+39${CONTACTS.cell}`} className="hover:text-gold transition-colors" data-testid="location-phone">{CONTACTS.cellFormatted}</a>} />
              <InfoRow icon={<MessageCircle size={18} />} title={t("loc_wa")} text={<a href={`tel:${CONTACTS.fixed}`} className="hover:text-gold transition-colors" data-testid="location-tel-fisso">{CONTACTS.fixedFormatted}</a>} />
              <InfoRow icon={<Clock size={18} />} title={t("loc_hours")} text={t("loc_hours_value")} />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={CONTACTS.googleMapsDirUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-btn"
                className="inline-flex items-center justify-center gap-3 bg-ember hover:bg-emberDeep text-white px-6 py-3.5 rounded-full font-medium transition-colors duration-300"
              >
                {t("loc_directions")} <ExternalLink size={16} />
              </a>
              <a
                href={`https://wa.me/${CONTACTS.cellIntl}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 border border-[#25D366] hover:bg-[#25D366] text-[#25D366] hover:text-white px-6 py-3.5 rounded-full font-medium transition-colors"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9 }}
            className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/3] lg:aspect-auto lg:h-[520px]"
            data-testid="location-map"
          >
            <iframe
              title="Mappa Drago Ramen"
              src="https://www.google.com/maps?q=Corso+Sicilia+7+Catania&output=embed"
              className="w-full h-full"
              style={{ border: 0, filter: "grayscale(1) contrast(1.1) invert(0.92) hue-rotate(180deg)" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/5 rounded-3xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon, title, text }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-full grid place-items-center bg-ash border border-white/10 text-gold shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-white/50">{title}</div>
        <div className="mt-1 text-white/85 leading-relaxed">{text}</div>
      </div>
    </div>
  );
}
